import React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import BundleItem from "../components/BundleItem";
import {
  createSteamPaymentSession,
  createStripePaymentSession,
  createPaypalPaymentSession,
} from "../services/payment";
import { getBundleById } from "../services/bundles";
import { getUserBrowserInfo } from "../services/userBrowserInfo";
import { buildCartItemFromBundle } from "../utils/cartItem";
import "../styles/bundles.css";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

/**
 * Execute reCAPTCHA v3 and return token
 */
const executeRecaptcha = async (action) => {
  if (!window.grecaptcha || !RECAPTCHA_SITE_KEY) {
    console.warn("reCAPTCHA not loaded or site key not configured");
    return null;
  }
  try {
    // grecaptcha.ready takes a callback, wrap it in a Promise
    await new Promise((resolve) => window.grecaptcha.ready(resolve));
    const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
    return token;
  } catch (error) {
    console.error("reCAPTCHA execution failed:", error);
    return null;
  }
};

class BundlePurchase extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      bundles: [],
      ipAddress: "",
    };
    this.handleCheckoutStripe = this.handleCheckoutStripe.bind(this);
    this.handleCheckoutPaypal = this.handleCheckoutPaypal.bind(this);
    this.handleCheckoutSteam = this.handleCheckoutSteam.bind(this);
    this.goToHomePage = this.goToHomePage.bind(this);
    this.handleAmountChange = this.handleAmountChange.bind(this);
  }

  async componentDidMount() {
    const { cartItems } = this.props;
    this.setState({ loading: true });

    if (cartItems.length > 0) {
      // Deep copy of the cart items
      const copiedItems = JSON.parse(JSON.stringify(...cartItems));
      this.setState({ bundles: [copiedItems] });
    } else {
      await this.getBundleById(this.props.match.params?.id);
    }
    await this.getIpAddress();

    this.setState({ loading: false });
  }

  goToHomePage() {
    this.props.history.push("/");
  }

  async getBundleById(bundleId) {
    if (!bundleId) {
      alert("No valid selected bundle was found");
      this.goToHomePage();
      return;
    }
    try {
      const bundle = await getBundleById(bundleId);
      if (!bundle) {
        alert("No valid selected bundle was found");
        this.goToHomePage();
        return;
      }

      this.setState({ bundles: [bundle] });
      const quantity = bundle?.is_custom ? bundle.sansar_dollar_amount : 1;
      this.dispatchCartItemsFromBundleChanges(bundle, quantity);

    } catch (error) {}
  }

  dispatchCartItemsFromBundleChanges(bundle, quantity) {
    const { dispatch } = this.props;

    dispatch({ type: "cartItems/clean" });
    const cartItem = buildCartItemFromBundle(bundle, quantity || 1);
    dispatch({ type: "cartItems/add", payload: cartItem });

    const totalPrice = parseFloat(bundle.price);
    dispatch({ type: "totalPrice/update", payload: totalPrice });
  }

  async getIpAddress() {
    if (!this.state.ipAddress && !this.props.userIsFromCognito) {
      try {
        const { ip } = await getUserBrowserInfo();
        this.setState({ ipAddress: ip });
      } catch (error) {}
    }
  }

  async handleCheckoutSteam(event) {
    event.preventDefault();
    const { cartItems, accessToken } = this.props;
    const { ipAddress } = this.state;
    if (!accessToken) {
      alert("Please log in to continue");
      return;
    }
    this.setState({ loading: true });
    try {
      const payload = {
        ip_address: ipAddress,
        cart_items: cartItems.map((item) => ({
          id: item.id,
          quantity: item.quantity,
        })),
      }
      const { url } = await createSteamPaymentSession(payload, accessToken);
      if (url) window.location.href = url;
    } catch (error) {
      alert("An error occurred while requesting your payment");
    } finally {
      this.setState({ loading: false });
    }
  }

  async handleCheckoutStripe(event) {
    event.preventDefault();
    const { cartItems, accessToken } = this.props;
    if (!accessToken) {
      alert("Please log in to continue");
      return;
    }
    this.setState({ loading: true });
    try {
      // Execute reCAPTCHA before payment
      const recaptchaToken = await executeRecaptcha("purchase");

      const payload = {
        cart_items: cartItems.map((item) => ({
          id: item.id,
          quantity: item.quantity,
        })),
        recaptcha_token: recaptchaToken,
      }
      const { url } = await createStripePaymentSession(payload, accessToken);
      if (url) window.location.href = url;
    } catch (error) {
      // Surface server error message if available (e.g., CAPTCHA or rate limit errors)
      // Handle both {errors: {message}} and {errors: [{error_description}]} formats
      const errors = error?.response?.data?.errors;
      const serverMessage = errors?.message ||
        (Array.isArray(errors) && errors[0]?.error_description) ||
        null;
      alert(serverMessage || "An error occurred while requesting your payment");
    } finally {
      this.setState({ loading: false });
    }
  }

  async handleCheckoutPaypal(event) {
    event.preventDefault();
    const { cartItems, accessToken } = this.props;
    if (!accessToken) {
      alert("Please log in to continue");
      return;
    }
    this.setState({ loading: true });
    try {
      // Execute reCAPTCHA before payment
      const recaptchaToken = await executeRecaptcha("purchase");

      const payload = {
        cart_items: cartItems.map((item) => ({
          id: item.id,
          quantity: item.quantity,
        })),
        recaptcha_token: recaptchaToken,
      }
      const { url } = await createPaypalPaymentSession(payload, accessToken);
      if (url) window.location.href = url;
    } catch (error) {
      // Surface server error message if available (e.g., CAPTCHA or rate limit errors)
      // Handle both {errors: {message}} and {errors: [{error_description}]} formats
      const errors = error?.response?.data?.errors;
      const serverMessage = errors?.message ||
        (Array.isArray(errors) && errors[0]?.error_description) ||
        null;
      alert(serverMessage || "An error occurred while requesting your payment");
    } finally {
      this.setState({ loading: false });
    }
  }

  handleAmountChange(bundleId) {
    const { bundles } = this.state;
    return (usdAmount, ssdAmount) => {
      const formattedPrice = usdAmount === "" ? "" : `$${usdAmount.toFixed(2)}`;
      const bundle = bundles.find((bundle) => bundle.id === bundleId);
      if (bundle) {
        const quantity = Number(ssdAmount);
        bundle.price = usdAmount;
        bundle.formatted_price = formattedPrice;
        bundle.quantity = quantity;

        this.setState({ bundles: [bundle] });
        this.dispatchCartItemsFromBundleChanges(bundle, quantity);
      }
    }
  }

  render() {
    const { handleCheckoutStripe, handleCheckoutPaypal,
      handleCheckoutSteam, goToHomePage, handleAmountChange } = this;
    const { userIsFromCognito, userIsLoggedIn } = this.props;
    const { loading, bundles } = this.state;

    return (
      <section>
        <h1>
          Complete your purchase
        </h1>
        <div className='bundle-purchase-info mt-8'>
          <div>
            <b>You are buying</b>
          </div>
          <div>
            <button onClick={() => goToHomePage()} className='go-back-button'>
              <b>Change amount</b>
            </button>
          </div>
        </div>
        <div className='bundles'>
          {bundles.map((bundle) => (
            <div>
              {
                bundle?.is_custom ? (
                  <div>You can purchase a custom amount of Sansar Dollars (minimum 50 Sansar dollars)</div>
                ) : null
              }
              <div key={bundle.id} className='bundle'>
                <BundleItem
                  amount={bundle.sansar_dollar_amount}
                  onAmountChange={handleAmountChange(bundle.id)}
                  isCustom={bundle?.is_custom}
                  isEditable={true} />
                <div>
                  <b>{ bundle.formatted_price }</b>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: "16px" }}>
          <b>Payment method</b>
        </div>
        {userIsLoggedIn && userIsFromCognito && (
          <div>
            <button disabled={loading} onClick={ handleCheckoutStripe } className='payment-checkout-button mt-2'>
              <span>Pay with Stripe</span>
              <img src='/stripe-logo.png' alt='Stripe logo' className='payment-checkout-button-payment-logo'></img>
            </button>
          </div>
        )}
        {userIsLoggedIn && userIsFromCognito && (
          <div>
            <button disabled={loading} onClick={ handleCheckoutPaypal } className='payment-checkout-button mt-2'>
              <span>Pay with Paypal</span>
              <img src='/paypal-logo.png' alt='Paypal logo' className='payment-checkout-button-payment-logo'></img>
            </button>
          </div>
        )}
        {userIsLoggedIn && !userIsFromCognito && (
          <div>
            <button disabled={loading} onClick={ handleCheckoutSteam } className='payment-checkout-button mt-2'>
              <span>Pay with Steam</span>
              <img src='/steam-logo.png' alt='Steam logo' className='payment-checkout-button-payment-logo'></img>
            </button>
          </div>
        )}
      </section>
    )
  }
}

const mapStateToProps = (state) => {
  return {
    cartItems: state.cart.cartItems,
    userIsFromCognito: state.user.userIsFromCognito,
    userIsLoggedIn: state.user.userIsLoggedIn,
    accessToken: state.user.accessToken,
  }
};

export default connect(mapStateToProps)(withRouter(BundlePurchase));
